import React, { Component } from 'react';
import { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import _ from 'underscore';
import { Select } from 'antd';
import { Image } from '../../../../Reusable/Image';
import { Text } from '../../../../Reusable/Text';
import { Flex, FlexColumn } from '../../../../Reusable/Container';
import { LabeledInput } from '../../../../Reusable/Input';
import { Button, TextButton } from '../../../../Reusable/Button';
import { ExpenseCategory } from '../../../../Reusable/ExpenseCategory';
import { toCurrency, toNumberStr, isCodatUser, getCodatPlatformName } from '../../../../../Utils/util';
import TableView from '../../../../Reusable/TableView';
import { ErrorAlert } from '../../../../Reusable/Alert';
import { apiPOSTReq } from '../../../../../Utils/api';
import environment from '../../../../../environment';
import DeleteIcon from '../../../../../Images/delete.svg';
import Add from '../../../../../Images/add.svg';
import { cloneDeep } from 'lodash';
import hexToRgba from 'hex-to-rgba';

const { Option } = Select;

// Priority map for expense categories
const priorityMap = {
    "Accounts Payable": 2,
    "Accounts Receivable": 3,
    "Bank": 4,
    "Cost of Goods Sold": 5,
    "Credit Card": 6,
    "Equity": 7,
    "Expense": 0,
    "Fixed Asset": 8,
    "Income": 9,
    "Long Term Liability": 10,
    "Other Asset": 11,
    "Other Current Asset": 12,
    "Other Current Liability": 13,
    "Other Expense": 1,
    "Other Income": 14,
};

class EditExpenseCategorization extends Component {
    constructor(props) {
        super(props);
        this.state = {
            bill: this.initializeBill(this.props.bill || { line: [] }),
            categories: this.props.categories || [],
            editMode: this.props.editMode !== undefined ? this.props.editMode : true,
            categoriesLoading: !this.props.categories || this.props.categories.length === 0,
            customers: [],
            customersLoading: false,
        };
    }

    initializeBill = (bill) => {
        if (!bill) return { line: [] };

        const initializedBill = cloneDeep(bill);
        if (initializedBill.line) {
            initializedBill.line = initializedBill.line.map((line, index) => ({
                ...line,
                itemId: line.itemId || this.generateUniqueId(), // Keep existing ID if present
                lineIndex: index
            }));
        }
        return initializedBill;
    }

    componentDidMount() {
        // Initialize line items with unique IDs
        const { bill } = this.state;
        if (bill.line) {
            bill.line.forEach((line, i) => {
                if (!line.itemId) line.itemId = this.generateUniqueId();
            });
        }

        // If categories weren't provided as props, fetch them
        if (!this.props.categories || this.props.categories.length === 0) {
            this.fetchCOA({});
        }

        // Fetch customers for codat users
        if (isCodatUser(this.props.store)) {
            this.fetchCustomers({ pagination: { current: 1, pageSize: 1000 } });
        }
    }

    componentDidUpdate(prevProps) {
        // Update bill if it changes
        if (prevProps.bill !== this.props.bill) {
            const updatedBill = { ...this.props.bill };
            // Ensure all line items have unique IDs
            if (updatedBill.line) {
                updatedBill.line.forEach((line, i) => {
                    if (!line.itemId) line.itemId = this.generateUniqueId();
                });
            }
            this.setState({ bill: updatedBill });
        }

        // Update categories if they're provided as props and change
        if (prevProps.categories !== this.props.categories && this.props.categories) {
            this.setState({ categories: this.props.categories });
        }

        // Update editMode if it changes
        if (prevProps.editMode !== this.props.editMode) {
            this.setState({ editMode: this.props.editMode });
        }
    }

    /**
     * Cleans description by replacing newlines with spaces
     * @param {string} description - The description text
     * @returns {string} Cleaned description
     */
    cleanDescription = (description) => {
        if (!description) return '';
        return description.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    }

    /**
     * Generates a unique ID for line items
     * @returns {string} A unique identifier
     */
    generateUniqueId = () => {
        return `line_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Fetches Chart of Accounts (categories) from the API
     * @param {Object} options - Options for the fetch request
     * @param {string} options.searchStr - Optional search string to filter categories
     */
    fetchCOA = ({ searchStr }) => {
        const headers = {
            AionCurrentBiz: this.props.store.BusinessUniqueKey,
            AionAuth: this.props.store.UAM.encryptedAuthHeader
        };

        const body = {
            "size": 1000,
            "page": 0,
            "ActiveRecords": true,
            "sortFieldName": "Code",
            "sortDirection": "ASC"
        };

        if (searchStr) {
            body.searchStr = searchStr;
        }

        this.setState({ categoriesLoading: true });

        apiPOSTReq(`${environment.bbBaseUrl}/bk/getCategories`, headers, body, (err, resp) => {
            try {
                const data = resp;
                if (data.result) {
                    const customCOAList = data.customCOAList.filter(x =>
                        x.coatype !== 'Accounts Payable' && x.coatype !== 'Accounts Receivable'
                    );

                    let categories = (data.systemCOAList || []).concat(customCOAList);
                    this.setState({
                        categories: categories ? categories.sort((a, b) => {
                            if (priorityMap[a.coatype] < priorityMap[b.coatype]) return -1;
                            else return 1;
                        }).map(x => ({
                            ...x,
                            key: x.id,
                        })) : [],
                        categoriesLoading: false
                    });
                } else {
                    this.setState({ categoriesLoading: false });
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                this.setState({ categoriesLoading: false });
                console.log("ERRR getCategories", error, err, resp);
                ErrorAlert({ description: error.message });
            }
        });
    }

    /**
     * Fetches customers from the API for codat users
     * @param {Object} options - Options for the fetch request
     */
    fetchCustomers = (options) => {
        let { pagination, searchTerm } = options;
        const headers = {
            AionCurrentBiz: this.props.store.BusinessUniqueKey,
            AionAuth: this.props.store.UAM.encryptedAuthHeader
        };
        const body = {
            "size": pagination.pageSize,
            "page": pagination.current - 1
        };
        if (searchTerm) {
            body.searchStr = searchTerm;
        }

        this.setState({ customersLoading: true });

        apiPOSTReq(`${environment.iveBaseUrl}/ive/bc/getActiveCustomers`, headers, body, (err, resp) => {
            try {
                const data = resp;
                console.log("getActiveCustomers", resp);
                if (data.result) {
                    this.setState({
                        customers: data.customers || [],
                        customersLoading: false
                    });
                } else {
                    this.setState({ customersLoading: false });
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                this.setState({ customersLoading: false });
                console.log("ERRR getActiveCustomers", error, err, resp);
                ErrorAlert({ description: error.message });
            }
        });
    }

    /**
     * Handles search functionality for Chart of Accounts
     * @param {string} value - The search string
     */
    handleSearchCOA = value => {
        this.fetchCOA({ searchStr: value });
    }

    /**
     * Adds a new line item row to the bill
     */
    addLineItemRow = () => {
        let bill = { ...this.state.bill };
        let billLine = bill.line || [];

        bill.line = billLine.concat([{
            itemId: this.generateUniqueId(),
            lineNum: billLine.length,
            unsavedItem: true
        }]);

        this.setState({ bill }, () => {
            // Call the parent's onBillUpdate if provided
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Removes a line item row from the bill
     * @param {number} id - The index of the line item to remove
     */
    removeLineItemRow = (id) => {
        let bill = { ...this.state.bill };
        if (!bill.line || bill.line.length <= 1) {
            bill.line = [];
            this.setState({ bill }, () => {
                if (this.props.onBillUpdate) {
                    this.props.onBillUpdate(bill);
                }
            });
            return;
        }

        bill.line.splice(id, 1);
        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Handles selection of an expense category for a line item
     * @param {string} value - The selected category ID
     * @param {number} index - The index of the line item
     */
    handleSelectCategory = (value, index) => {
        let bill = { ...this.state.bill };
        const { categories } = this.state;
        const category = categories.find(item => item.id === value);

        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem.accountBasedExpenseLineDetail = {
                accountRef: {
                    value: category.id,
                    name: category.categoryName,
                }
            };
        } else {
            line.push({
                itemId: index,
                accountBasedExpenseLineDetail: {
                    accountRef: {
                        value: category.id,
                        name: category.categoryName,
                    }
                }
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Handles selection of a customer for a line item (optional field)
     * @param {string} value - The selected customer ID
     * @param {number} index - The index of the line item
     */
    handleSelectCustomer = (value, index) => {
        let bill = { ...this.state.bill };
        const { customers } = this.state;
        const customer = customers.find(item => item.id === value);

        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        if (!customer) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem.customerRef = {
                id: customer.id,
                name: customer.displayName,
            };
        } else {
            line.push({
                itemId: index,
                customerRef: {
                    id: customer.id,
                    name: customer.displayName,
                }
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Handles billable switch change for a line item (optional field)
     * @param {string} value - The selected switch value ('Yes' or 'No')
     * @param {number} index - The index of the line item
     */
    handleBillableChange = (value, index) => {
        let bill = { ...this.state.bill };
        const line = bill.line || [];

        // Ensure the line array has enough items
        if (index >= line.length) {
            return;
        }

        // Convert switch value to boolean (matching UserProfile pattern)
        const booleanValue = value === 'Yes';

        // Get the line item at the specific index
        const selectedItem = line[index];
        if (selectedItem) {
            // Update the existing item
            selectedItem.billToCustomer = booleanValue;
        }

        // Update the bill state
        this.setState({ bill: { ...bill } }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Handles text changes for line item descriptions
     * @param {Event} event - The input event
     * @param {number} index - The index of the line item
     */
    handleLineItemTextChange = (event, index) => {
        let bill = { ...this.state.bill };
        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem[event.target.id] = event.target.value;
        } else {
            line.push({
                itemId: index,
                [event.target.id]: event.target.value,
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Handles amount changes for line items
     * @param {string} value - The amount value
     * @param {number} index - The index of the line item
     */
    handleLineItemAmountTextChange = (value, index) => {
        let bill = { ...this.state.bill };
        const line = bill.line || [];
        if (line.length <= index) {
            return;
        }

        const selectedItem = line[index];
        if (selectedItem) {
            selectedItem.amount = value;
        } else {
            line.push({
                itemId: index,
                amount: value,
            });
        }

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
            this.updateTotals();
        });
    }

    /**
     * Calculates the total amount of all line items
     * @returns {Object} The total amount
     */
    getLineItemTotals = () => {
        const { bill } = this.state;
        const reducer = (accumulator, item) => accumulator + parseFloat(item.amount || 0);
        const total = (bill.line || []).reduce(reducer, 0);
        return { totalAmt: total };
    }

    /**
     * Updates the total and balance in the bill based on line items
     */
    updateTotals = () => {
        let bill = { ...this.state.bill };
        const totals = this.getLineItemTotals();
        bill.totalAmt = totals.totalAmt || 0;
        bill.balance = bill.totalAmt;

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
     * Checks if any line items are missing expense categories
     * @returns {boolean} True if at least one line item has a category but not all line items do
     */
    checkForIncompleteCOA = () => {
        const { editMode, bill } = this.state;
        const filteredCOAs = (bill.line || []).filter(line =>
            line.accountBasedExpenseLineDetail ? line.accountBasedExpenseLineDetail.accountRef : false
        );
        // Return true if all line items have not been categorized and at least one should be categorized
        return (editMode || ((filteredCOAs.length != bill.line.length) && filteredCOAs.length > 0));
    }

    /**
     * Applies the same expense category to all line items
     * @param {Object} coa - The category reference to apply to all line items
     */
    applyCOAToAll = (coa) => {
        let bill = { ...this.state.bill };
        (bill.line || []).forEach(line => {
            if (line.accountBasedExpenseLineDetail) line.accountBasedExpenseLineDetail.accountRef = coa;
            else line.accountBasedExpenseLineDetail = { accountRef: coa };
        });

        this.setState({ bill }, () => {
            if (this.props.onBillUpdate) {
                this.props.onBillUpdate(bill);
            }
        });
    }

    /**
 * Validates all line items to ensure required fields are filled
 * @returns {Object} Validation result with isValid boolean and errors array
 */
    validateLineItems = () => {
        const { bill } = this.state;
        const errors = [];
        let isValid = true;

        if (!bill.line || bill.line.length === 0) {
            errors.push("At least one line item is required");
            return { isValid: false, errors };
        }

        bill.line.forEach((line, index) => {
            const lineNumber = index + 1;

            // Validate description
            if (!line.description || line.description.trim() === '') {
                errors.push(`Line item ${lineNumber}: Description is required`);
                isValid = false;
            }

            // Validate amount
            if (!line.amount) {
                errors.push(`Line item ${lineNumber}: Amount is required and must be greater than 0`);
                isValid = false;
            }

            // Validate expense account
            if (!line.accountBasedExpenseLineDetail ||
                !line.accountBasedExpenseLineDetail.accountRef ||
                !line.accountBasedExpenseLineDetail.accountRef.value) {
                errors.push(`Line item ${lineNumber}: Expense account is required`);
                isValid = false;
            }
        });

        return { isValid, errors };
    }

    /**
     * Handles the update button click with validation
     */
    handleUpdateClick = () => {
        const { bill } = this.state;
        const validation = this.validateLineItems();

        if (!validation.isValid) {
            // Show validation errors
            const errorMessage = validation.errors.join('\n');
            ErrorAlert({
                title: 'Validation Error',
                description: errorMessage
            });
            return;
        }

        // If validation passes, proceed with update
        this.props.updateBillVendor({ bill: bill, loadStep: "Edit" });
    }

    /**
     * Gets the column configuration for the line items table
     * @returns {Array} The column configuration
     */
    getColumns = () => {
        const { categories, customers, editMode, bill } = this.state;
        const { theme, store } = this.props;
        const isCodat = isCodatUser(store);
        const platformName = isCodat ? getCodatPlatformName(store) : '';

        const columns = [
            {
                title: 'Line Items',
                dataIndex: 'lineItem',
                render: (_, lineItem, i) => {
                    const { accountBasedExpenseLineDetail, customerRef, billToCustomer, description, amount } = lineItem;
                    const coa = accountBasedExpenseLineDetail ? accountBasedExpenseLineDetail.accountRef || {} : {};

                    return (
                        <Flex gap='16px' centerH style={{ padding: '12px 0' }}>
                            <Flex gap='16px' centerH centerV style={{ height: '25px', width: '25px', background: hexToRgba(theme.colors.primary2, 0.1), padding: "10px", borderRadius: '50%' }}>
                                <Text>{i + 1}</Text>
                            </Flex>
                            <FlexColumn gap='16px' style={{ flex: 1 }}>
                                {/* First row: Description */}
                                <Flex gap='16px'>
                                    <div style={{ flex: 2 }}>
                                        {editMode ? (
                                            <LabeledInput
                                                nomargin
                                                label="Description"
                                                type={'text-area'}
                                                id="description"
                                                placeholder="Add text"
                                                autoSize={{ minRows: 1, maxRows: 3 }}
                                                defaultValue={this.cleanDescription(description)}
                                                onBlur={(e) => this.handleLineItemTextChange(e, i)}
                                            />
                                        ) : (
                                            <LabeledInput
                                                nomargin
                                                label="Description"
                                                type={"read-only"}
                                                value={this.cleanDescription(description)}
                                            />
                                        )}
                                    </div>
                                </Flex>
                                {/* Second row: Amount and Expense Account */}
                                <Flex gap='16px'>
                                    <div style={{ flex: 2 }}>
                                        {(editMode || !coa.value) ? (
                                            <LabeledInput
                                                nomargin
                                                label="Expense Account"
                                                id="category"
                                                type="select"
                                                placeholder="Select account"
                                                defaultValue={coa.name}
                                                onChange={e => this.handleSelectCategory(e, i)}
                                                noBorder={!editMode}
                                                showSearch
                                                filterOption={(input, option) => {
                                                    return option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                                }}
                                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                className='parent-node-select-dropdown-menu'
                                            >
                                                {categories && categories.filter(category => !category.coatype.includes('Liability')).map(category => (
                                                    <Option key={category.id} id="category" name={category.categoryName} value={category.id}>
                                                        <ExpenseCategory selected={coa.value == category.id} category={category} hideCategory={true} theme={theme} />
                                                    </Option>
                                                ))}
                                            </LabeledInput>
                                        ) : (
                                            <LabeledInput
                                                nomargin
                                                label="Expense Account"
                                                id="category"
                                                type={"read-only"}
                                                className="no-left-padding"
                                                value={coa.name}
                                            />
                                        )}
                                        {(coa.value) && (i == 0 && bill.line.length > 1) && this.checkForIncompleteCOA() && (
                                            <Text light><a onClick={() => this.applyCOAToAll(coa)}>Apply this</a> to all line items</Text>
                                        )}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {editMode ? (
                                            <LabeledInput
                                                nomargin
                                                label="Amount"
                                                id="amount"
                                                type="amount"
                                                placeholder="5000"
                                                value={amount}
                                                onBlur={e => {
                                                    const fVal = toNumberStr(e.target.value);
                                                    this.handleLineItemAmountTextChange(fVal, i);
                                                }}
                                                onPressEnter={e => {
                                                    const fVal = toNumberStr(e.target.value);
                                                    this.handleLineItemAmountTextChange(fVal, i);
                                                }}
                                            />
                                        ) : (
                                            <LabeledInput
                                                nomargin
                                                label="Amount"
                                                type={"read-only"}
                                                prefixText="$"
                                                value={`$${toCurrency(amount)}`}
                                            />
                                        )}
                                    </div>

                                </Flex>

                                {/* Third row: Codat-specific fields (optional) */}
                                {isCodat && (
                                    <Flex gap='16px'>
                                        <div style={{ flex: 2 }}>
                                            {editMode ? (
                                                <LabeledInput
                                                    nomargin
                                                    label={`${platformName} Customer`}
                                                    type="select"
                                                    placeholder="Select customer"
                                                    value={customerRef ? customerRef.id : undefined}
                                                    onChange={e => this.handleSelectCustomer(e, i)}
                                                    showSearch
                                                    filterOption={(input, option) => {
                                                        return option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                                    }}
                                                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                    className='parent-node-select-dropdown-menu'
                                                    optional
                                                >
                                                    {customers.map(customer => (
                                                        <Option key={customer.id} name={customer.displayName} value={customer.id}>
                                                            {customer.displayName}
                                                        </Option>
                                                    ))}
                                                </LabeledInput>
                                            ) : (
                                                <LabeledInput
                                                    nomargin
                                                    label={`${platformName} Customer`}
                                                    type={"read-only"}
                                                    value={customerRef ? customerRef.name : ''}
                                                />
                                            )}
                                        </div>
                                        {
                                            customerRef && customerRef.id &&
                                            <div style={{ flex: 1 }}>
                                                {editMode ? (
                                                    <LabeledInput
                                                        type='switch'
                                                        label={`Bill to Customer`}
                                                        nomargin
                                                        switchNames={['No', 'Yes']}
                                                        onChange={(value) => this.handleBillableChange(value, i)}
                                                        value={billToCustomer === true ? 'Yes' : 'No'}
                                                        key={`billable-${lineItem.itemId}-${billToCustomer}`}
                                                        optional
                                                    />
                                                ) : (
                                                    <LabeledInput
                                                        nomargin
                                                        label={`${platformName} Billable`}
                                                        type={"read-only"}
                                                        value={billToCustomer === true ? 'Yes' : 'No'}
                                                    />
                                                )}
                                            </div>
                                        }
                                    </Flex>
                                )}
                            </FlexColumn>

                            {/* Delete icon - centered to both rows */}
                            {editMode && bill.line.length > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                    <img
                                        width='24px'
                                        height='24px'
                                        style={{ cursor: 'pointer' }}
                                        src={DeleteIcon}
                                        onClick={() => this.removeLineItemRow(i)}
                                    />
                                </div>
                            )}
                        </Flex>
                    );
                }
            }
        ];

        return columns;
    }

    render() {
        const { bill, editMode, categoriesLoading, customersLoading } = this.state;
        const { titleText, descText, showTotal, theme } = this.props;

        return (
            <FlexColumn gap='16px' style={{ background: hexToRgba(theme.colors.primary2, 0.05), padding: "30px 20px", borderRadius: '15px', marginTop: '15px' }}>
                <TableView
                    id="line-item-table"
                    key={"line-item-table" + (bill.line || []).length}
                    tableLayout='auto'
                    columns={this.getColumns()}
                    dataSource={bill.line}
                    rowKey="itemId"
                    titleText="Edit line items"
                    descText="Update description, amount and expense account for each line item"
                    borderView
                    loading={categoriesLoading || customersLoading}
                />

                {showTotal && (
                    <FlexColumn centerVertically left style={{ marginRight: 24, marginTop: 8 }} gap='8px'>
                        <Flex centerHorizontally gap='4px'>
                            <Text size='18px' weight={500}>TOTAL {`$${toCurrency(this.getLineItemTotals().totalAmt)}`}</Text>
                        </Flex>
                    </FlexColumn>
                )}

                {editMode && (
                    <TextButton
                        onClick={() => this.addLineItemRow()}
                        margin="0"
                        rightIcon={<Image src={Add} />}
                        text="ADD LINE ITEM"
                    />
                )}

                <FlexColumn left>
                    <Button
                        solid
                        onClick={this.handleUpdateClick} 
                        text={"UPDATE"}
                    />
                </FlexColumn>

            </FlexColumn>
        );
    }
}

// Default props
EditExpenseCategorization.defaultProps = {
    bill: { line: [] },
    categories: null,
    editMode: true,
    showTotal: true,
    titleText: null,
};

function mapStateToProps(state) {
    return {
        store: state.aionAppReducer
    };
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(EditExpenseCategorization));